import { api } from './api';
import type {
  ApiEnvelope, AuditFields, EngineListParams, PaginatedEnvelope, Party,
} from './notification-engine.types';

const BASE = '/api/v1/recipient-lists';

/**
 * A recipient_list row — a named, reusable group of parties.
 *
 * `isDoNotContact` inverts what the list means: its parties become endpoints
 * that must NOT be contacted rather than an audience. That is how an EXTERNAL
 * party says stop, since it has no user record and so no preferences to set.
 */
export interface RecipientList extends AuditFields {
  _id: string;
  recipientListCode: string;
  description?: string | null;
  parties: Party[];
  isDoNotContact?: boolean;
}

export type RecipientListCreateRequest =
  Omit<RecipientList, '_id' | keyof AuditFields> & { appId?: string | null };
export type RecipientListUpdateRequest = Partial<RecipientListCreateRequest>;

export const recipientListsService = {
  list: async (params?: EngineListParams): Promise<PaginatedEnvelope<RecipientList>> => {
    const response = await api.get<PaginatedEnvelope<RecipientList>>(BASE, { params });
    return response.data;
  },
  getById: async (id: string): Promise<RecipientList> => {
    const response = await api.get<ApiEnvelope<RecipientList>>(`${BASE}/${id}`);
    return response.data.data;
  },
  create: async (data: RecipientListCreateRequest): Promise<RecipientList> => {
    const response = await api.post<ApiEnvelope<RecipientList>>(BASE, data);
    return response.data.data;
  },
  update: async (id: string, data: RecipientListUpdateRequest): Promise<RecipientList> => {
    const response = await api.put<ApiEnvelope<RecipientList>>(`${BASE}/${id}`, data);
    return response.data.data;
  },
  /** Retire without deleting: the runtime reads only active rows. */
  deactivate: async (id: string): Promise<RecipientList> => {
    const response = await api.post<ApiEnvelope<RecipientList>>(`${BASE}/${id}/deactivate`);
    return response.data.data;
  },
  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiEnvelope<{ message: string }>>(`${BASE}/${id}`);
    return response.data.data;
  },
};
